import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  geo?: {
    placename?: string;
    region?: string;
    position?: string;
  };
  structuredData?: Record<string, any> | Array<Record<string, any>>;
  noindex?: boolean;
  language?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  geo,
  structuredData,
  noindex = false,
  language = 'en'
}: SEOProps) {
  console.log('🔍 [SEO] Rendering SEO component:', { title, description, url });
  console.log('🔍 [SEO] Is server-side rendering:', typeof window === 'undefined');
  const siteUrl = 'https://campwhiz.com';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  // Default Open Graph image if none provided
  const defaultOgImage = '/campwhiz-ogimage.png';
  const fullImageUrl = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : `${siteUrl}${defaultOgImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="CampWhiz" />
      <meta property="og:locale" content={language} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:site" content="@campwhiz" />
      <meta name="twitter:url" content={fullUrl} />

      {/* Geo Tags */}
      {geo?.placename && <meta name="geo.placename" content={geo.placename} />}
      {geo?.region && <meta name="geo.region" content={geo.region} />}
      {geo?.position && <meta name="geo.position" content={geo.position} />}
      {geo?.position && <meta name="ICBM" content={geo.position} />}

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#3B82F6" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="CampWhiz" />

      {/* Structured Data */}
      {structuredData && (
        <>
          {Array.isArray(structuredData) ? (
            structuredData.map((data, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(data)}
              </script>
            ))
          ) : (
            <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>
          )}
        </>
      )}

      {/* Preconnect to important domains */}
      <link rel="preconnect" href="https://weudqolihrchgtfpvexi.supabase.co" />
      <link rel="preconnect" href="https://maps.googleapis.com" />
      <link rel="preconnect" href="https://api.bigdatacloud.net" />
      
      {/* Web App Manifest */}
      <link rel="manifest" href="/manifest.json" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="CampWhiz" />
      <meta name="copyright" content="CampWhiz" />
      <meta name="application-name" content="CampWhiz" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Security headers must be set by the server, not via meta tags */}
      
      {/* DNS Prefetch for external resources */}
      <link rel="dns-prefetch" href="//weudqolihrchgtfpvexi.supabase.co" />
      <link rel="dns-prefetch" href="//maps.googleapis.com" />
      <link rel="dns-prefetch" href="//api.bigdatacloud.net" />
    </Helmet>
  );
} 